import React from 'react';
import { Card, Badge, Button } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import { theme } from '../theme';
import { useAuth } from '../context/AuthContext';

/**
 * CommunityCard displays a summary card for a community in the list view.
 *
 * Props:
 *   community  - API response object: { id, name, timezone, currency, metadata, created_at, updated_at,
 *                                        buildings?, community_assets?, community_datasets? }
 *   onDelete   - async (communityId) => void   (called after user confirms)
 */
const CommunityCard = ({ community, onDelete }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { canEdit } = useAuth();

  const meta = community.metadata || {};
  const status = meta.status || 'setup';
  const location = meta.location || '';
  const description = meta.description || '';

  const getStatusBadge = (s) => {
    const badges = {
      active: { bg: 'success', text: t('community_card.status_active') },
      setup: { bg: 'warning', text: t('community_card.status_setup') },
      inactive: { bg: 'secondary', text: t('community_card.status_inactive') },
    };
    return badges[s] || badges.inactive;
  };

  const statusBadge = getStatusBadge(status);

  const handleCardClick = () => {
    navigate(`/communities/${community.id}/dashboard`);
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    navigate(`/communities/${community.id}/edit`);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    if (onDelete) {
      onDelete(community.id);
    }
  };

  return (
    <Card
      className="h-100 shadow-sm hover-shadow transition-all"
      onClick={handleCardClick}
      style={{
        borderLeft: `4px solid ${theme.colors.primary.main}`,
        cursor: 'pointer',
      }}
    >
      <Card.Body>
        {/* Top section: title + status badge + action icons */}
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div className="flex-grow-1 me-2">
            <Card.Title className="mb-1">
              {community.name}
            </Card.Title>
            {location && (
              <Card.Subtitle className="text-muted">
                {location}
              </Card.Subtitle>
            )}
          </div>
          <div className="d-flex align-items-center gap-2">
            <Badge bg={statusBadge.bg}>{statusBadge.text}</Badge>
            {canEdit && (
              <Button
                variant="link"
                size="sm"
                className="p-1 text-muted"
                onClick={handleEdit}
                title={t('community_card.edit_title')}
                style={{ lineHeight: 1 }}
              >
                <FiEdit2 size={16} />
              </Button>
            )}
            {canEdit && onDelete && (
              <Button
                variant="link"
                size="sm"
                className="p-1"
                onClick={handleDelete}
                title={t('community_card.delete_title')}
                style={{ lineHeight: 1, color: theme.colors.semantic.danger }}
              >
                <FiTrash2 size={16} />
              </Button>
            )}
          </div>
        </div>

        {description && (
          <Card.Text className="text-muted small mb-3">
            {description}
          </Card.Text>
        )}

        <div className="row g-3 mb-3">
          <div className="col-6">
            <div className="text-center p-2 rounded" style={{ backgroundColor: theme.colors.neutral[100] }}>
              <div className="fw-bold" style={{ color: theme.colors.primary.main }}>
                {community.timezone || t('common.n_a')}
              </div>
              <div className="small text-muted">{t('community_card.timezone')}</div>
            </div>
          </div>
          <div className="col-6">
            <div className="text-center p-2 rounded" style={{ backgroundColor: theme.colors.neutral[100] }}>
              <div className="fw-bold" style={{ color: theme.colors.energy?.solar || '#FFB800' }}>
                {community.currency || 'EUR'}
              </div>
              <div className="small text-muted">{t('community_card.currency')}</div>
            </div>
          </div>
        </div>

        {meta.country && (
          <div className="small mb-3">
            <span className="text-muted">{t('community_card.country')}</span>
            <span className="ms-1 fw-semibold">{meta.country}</span>
          </div>
        )}
      </Card.Body>

      <Card.Footer className="text-muted small">
        {t('community_card.created')} {new Date(community.created_at).toLocaleDateString()}
        {community.updated_at && (
          <span className="ms-2">
            | {t('community_card.updated')} {new Date(community.updated_at).toLocaleDateString()}
          </span>
        )}
      </Card.Footer>
    </Card>
  );
};

export default CommunityCard;
